import { ColumnTypes } from '../../../../src/components/table/columnTypes';
import { createSelector } from '@opengov/compose-app/reselect';
import { BasicColumnTypes, IOgqlJobResult, IOgqlV1JobResult } from '@opengov/ogql-types';
import {
  EOgqlInfixColumnOperators,
  EReportColumnTypes,
  IReportColumn
} from '@opengov/reporting-types/src/reportColumns-immer';
import { produce } from 'immer';
// tslint:disable-next-line:max-line-length
import { selectAgGridColumnTypes } from '../../../reportExplorer/ReportDataTableAgGrid/selectors/selectAgGridColumnTypes';
import { selectDetailTableColumns } from '../../../reportExplorer/selectors/selectReportExplorerState';
import { getTableColumnTypeFromReportColumnType } from '../../helpers/semanticTypeToColumnType';
import { reportLocaleText } from '../../locale';
import {
  indicesOfHiddenColumns,
  removeHiddenData
} from '../../reportConfiguration/effects/reportQueryEffectResponders';
import {
  getCalculablesForLeftColumnAndOperator,
  getCustomCalculablesForLeftColumnAndOperator
} from '../basicCalculationUtils';
import { ICalculationPreviewTable } from '../components/CalculationPreviewTable';
import { getDateFormatFromColumn } from '../getDateFormatFromColumn';
import { basicCalculationSelector } from '../state/basicCalculationState';
import {
  CalculationColumnTypeSecondaryLabels,
  CalculationCustomMessageKeys,
  EAllowedCalculationTypes,
  ECalculationCustomValues
} from '../types';

export const selectBasicCalculation = createSelector(basicCalculationSelector, (state) => state.basicCalculation);

export const selectQueryResult = createSelector(basicCalculationSelector, (state) => state.queryResult);

const selectColumnDefs = createSelector(selectQueryResult, selectBasicCalculation, (result, calculationColumn) => {
  let columns = [];

  if (result) {
    const { jobResult } = result;
    const queryColumns =
      (jobResult as IOgqlJobResult['jobResult']).columns ||
      (jobResult as IOgqlV1JobResult['jobResult']).metadata.columns;

    let column = calculationColumn;

    if (calculationColumn.columnType === EReportColumnTypes.infix && !calculationColumn.displayName) {
      column = produce(calculationColumn, (newState) => {
        newState.displayName = 'New Column';
      });
    }

    columns =
      column.columnType === EReportColumnTypes.infix ? [column.leftColumn, column.rightColumn, column] : [column];
    columns = columns.map((column, i) => {
      const columnType = getTableColumnTypeFromReportColumnType(column);
      return {
        headerName: column.displayName,
        field: queryColumns[i].name,
        type: columnType,
        // Determines whether or not the column header and cells are right aligned
        isValueColumn: columnType !== ColumnTypes.Text ? true : false,
        highlight: i === 2
      };
    });
  }

  return columns;
});

const selectRowData = createSelector(selectQueryResult, (result) => {
  let rowData = [];

  if (result) {
    const { jobResult } = result;
    const { data } = jobResult;
    const queryColumns =
      (jobResult as IOgqlJobResult['jobResult']).columns ||
      (jobResult as IOgqlV1JobResult['jobResult']).metadata.columns;
    const removableIndices = indicesOfHiddenColumns(queryColumns);
    const rawRowData = removeHiddenData(data, removableIndices);
    const visibleColumns = (queryColumns as IOgqlJobResult['jobResult']['columns']).filter(
      (column, ndx) => !removableIndices.includes(ndx)
    );

    rowData = rawRowData
      .map((row) => {
        // when no data comes back the first row is an empty array so we need to filter it out
        if (!row.length) {
          return null;
        }
        return row.reduce((acc, value, index) => {
          acc[visibleColumns[index].name] = value;
          return acc;
        }, {});
      })
      .filter((row) => !!row);
  }

  return rowData;
});

export const selectCalculationPreviewTableProps = createSelector(
  basicCalculationSelector,
  reportLocaleText,
  selectColumnDefs,
  selectRowData,
  selectAgGridColumnTypes,
  (state, reportMessages, columnDefs, rowData, columnTypes): ICalculationPreviewTable => ({
    calculationPreviewTableProps: {
      rowData,
      columnDefs,
      gridContainerStyle: { height: '200px' },
      onGridReady: ({ api }) => {
        api.sizeColumnsToFit();
      },
      gridOptions: { columnTypes }
    },
    calculationPreviewTableStatus: state.calculationPreviewTableStatus,
    reportMessages
  })
);

export const buildValueOptionsFromColumns = (
  columns: IReportColumn[],
  allowedColumnTypes: string[] = (Object as any).values(EAllowedCalculationTypes)
) => {
  const optionCategories = columns
    .filter((c) => (Object as any).values(EAllowedCalculationTypes).includes(c.type) && c.hidden !== true)
    .reduce((categories, column: IReportColumn) => {
      let category = column.type;
      if (column.type === BasicColumnTypes.Year) {
        category = BasicColumnTypes.Date;
      }
      if (column.type === BasicColumnTypes.YearInterval || column.type === BasicColumnTypes.DayInterval) {
        category = BasicColumnTypes.Numeric;
      }
      const selectOption = {
        label: column.displayName,
        value: column.name,
        disabled: !allowedColumnTypes.includes(category),
        secondaryLabel:
          category === BasicColumnTypes.Date
            ? CalculationColumnTypeSecondaryLabels[getDateFormatFromColumn(column)]
            : CalculationColumnTypeSecondaryLabels[category]
      };

      if (categories[category]) {
        categories[category].items.push(selectOption);
      } else {
        const assignedCategory = { disabled: !allowedColumnTypes.includes(category), items: [selectOption] };
        categories[category] = assignedCategory;
      }
      return categories;
    }, {});
  Object.keys(optionCategories).forEach(
    (cat) =>
      optionCategories[cat].items &&
      (optionCategories[cat].items = optionCategories[cat].items.sort((a, b) => (b.label > a.label ? -1 : 1)))
  );
  return optionCategories;
};

export const selectValue1CustomOptions = createSelector(reportLocaleText, (reportMessages) => {
  return [
    {
      label: reportMessages.calculations.basicCalculationModal.customValueNumeric,
      value: ECalculationCustomValues.Numeric
    },
    {
      label: reportMessages.calculations.basicCalculationModal.customValuePercentage,
      value: ECalculationCustomValues.Percentage
    }
  ];
});

export const selectValue1Options = createSelector(
  selectValue1CustomOptions,
  selectDetailTableColumns,
  (customOptions, columns) => ({
    ...buildValueOptionsFromColumns(columns, (Object as any).values(EAllowedCalculationTypes)),
    custom: { disabled: false, items: customOptions }
  })
);

export const selectOperationOptions = createSelector(
  reportLocaleText,
  selectBasicCalculation,
  (localeText, calculation) => {
    const {
      calculations: {
        basicCalculationModal: { plusOperator, minusOperator, multipliedByOperator, dividedByOperator }
      }
    } = localeText;
    const operationOptions = {
      [EOgqlInfixColumnOperators.add]: { label: plusOperator, value: EOgqlInfixColumnOperators.add },
      [EOgqlInfixColumnOperators.subtract]: { label: minusOperator, value: EOgqlInfixColumnOperators.subtract },
      [EOgqlInfixColumnOperators.multiply]: { label: multipliedByOperator, value: EOgqlInfixColumnOperators.multiply },
      [EOgqlInfixColumnOperators.divide]: { label: dividedByOperator, value: EOgqlInfixColumnOperators.divide }
    };
    return calculation.leftColumn.type === BasicColumnTypes.Date ||
      calculation.leftColumn.type === BasicColumnTypes.Year ||
      calculation.leftColumn.type === BasicColumnTypes.DayInterval ||
      calculation.leftColumn.type === BasicColumnTypes.YearInterval
      ? [
          operationOptions[EOgqlInfixColumnOperators.add],
          operationOptions[EOgqlInfixColumnOperators.subtract],
          { ...operationOptions[EOgqlInfixColumnOperators.multiply], disabled: true },
          { ...operationOptions[EOgqlInfixColumnOperators.divide], disabled: true }
        ]
      : Object.values(operationOptions);
  }
);

export const selectValue2CustomOptions = createSelector(
  selectBasicCalculation,
  reportLocaleText,
  (basicCalculation, reportMessages) => {
    return getCustomCalculablesForLeftColumnAndOperator(
      basicCalculation.leftColumn.type,
      basicCalculation.operator
    ).map((calculable) => {
      return {
        label: reportMessages.calculations.basicCalculationModal[CalculationCustomMessageKeys[calculable]],
        value: calculable
      };
    });
  }
);

export const selectValue2Options = createSelector(
  selectDetailTableColumns,
  selectValue2CustomOptions,
  selectBasicCalculation,
  (columns, customOptions, basicCalculation) => {
    if (!basicCalculation.leftColumn.name || !basicCalculation.operator) {
      return {
        ...buildValueOptionsFromColumns(columns, (Object as any).values(EAllowedCalculationTypes)),
        custom: { disabled: false, items: customOptions }
      };
    }
    const calculables = getCalculablesForLeftColumnAndOperator(
      basicCalculation.leftColumn.type,
      basicCalculation.operator
    );
    return { ...buildValueOptionsFromColumns(columns, calculables), custom: { disabled: false, items: customOptions } };
  }
);
